const PASSWORD_REPLACEMENT = '******';
import ApiService from '@/services/apiService';
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
export default {
  name: 'CoreTenantBridgeConfigField',
  props: {
    bridgeType: {
      type: String,
      required: true
    },
    // eslint-disable-next-line vue/require-prop-types
    fieldConfig: {
      // type: Object,
      required: true
    },
    bridgeManifest: {
      type: Object,
      required: true
    },
    // the corresponding config-parameters for the bridge (NOT the bridge-config-object!)
    bridgeConfig: {
      type: Object,
      required: false,
      default: () => ({})
    },
    visibilityContext: {
      type: Object,
      required: false,
      default: null
    },
    bridgeConfigId: {
      type: Number,
      required: false,
      default: null
    },
    configKey: {
      type: String,
      required: true
    },
    isCreatedByTemplate: {
      type: Boolean,
      required: false,
      default: false
    },
    // eslint-disable-next-line vue/require-prop-types
    value: {
      required: false,
      default: () => undefined
    },
    bridgeHasValidParameters: {
      type: Boolean,
      required: true,
      default: true
    },
    bridgeHasValidParametersStrict: {
      type: Boolean,
      required: true
    },
    defaultIcon: {
      type: String,
      required: false,
      default: () => ''
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    // eslint-disable-next-line vue/require-prop-types
    dependsOnFieldValue: {
      required: false,
      default: null
    },
    // code that used this is commented out, remove it works fine without it
    // bridgeValidations: {
    //   type: Object,
    //   required: false,
    //   default: () => ({}),
    // },
    brigdeHasError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasVirtualDeviceKeyReaderPermission: {
      type: Boolean,
      required: false,
      default: false
    },
    dtwinDeviceId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data: function () {
    return {
      v: undefined,
      showPassword: false,
      autoCompleteItems: [],
      search: null,
      debouncedRequestAutoComplete: debounce(this.requestAutoComplete, 300),
      // see `updated` hook for filling stuff initially
      initialUpdate: false,
      emptyCustomHeaders: false,
      openHelpText: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    isVisible() {
      const visibleConfig = get(this.fieldConfig, 'visible');
      if (!visibleConfig || typeof visibleConfig !== 'object') {
        return true;
      }
      const ctx = this.visibilityContext != null ? {
        ...this.bridgeConfig,
        ...this.visibilityContext
      } : this.bridgeConfig;
      return Object.keys(visibleConfig).every(key => ctx[key] === visibleConfig[key]);
    },
    hasPrependIcon() {
      if (!this.bridgeHasValidParameters && this.isBrigdeHasError) {
        // enum and autocomplete never check for optional in prepend-inner template
        if (this.fieldConfig.type === 'enum' || this.fieldConfig.autocomplete) {
          return true;
        }
        return !!this.fieldConfig.optional ? false : true;
      } else if (this.bridgeHasValidParametersStrict) {
        return true;
      } else if (this.defaultIcon) {
        return true;
      }
      return false;
    },
    helpTextKey() {
      return `Bridges.quickReferences.BRIDGE-${this.bridgeType.replace(/^bridge-/, '')}.fieldHelp.${this.configKey}`;
    },
    hasHelpText() {
      return this.$i18n_te(this.helpTextKey);
    },
    fieldLabel() {
      return this.fieldConfig.name || this.configKey;
    },
    fieldConfigStringField() {
      return get(this.fieldConfig, 'stringOptions.field') || '';
    },
    isValidPassword() {
      return (this.fieldConfigStringField === 'password' || this.fieldConfigStringField === 'textarea' && this.fieldConfig.stringOptions.secure) && this.v !== PASSWORD_REPLACEMENT;
    },
    fieldConfigRequiresPermission() {
      return get(this.fieldConfig, 'stringOptions.requiresPermission') || false;
    },
    fieldConfigRequiredPermission() {
      return get(this.fieldConfig, 'stringOptions.requiredPermission') || '';
    },
    requiredRule() {
      return v => {
        if (v === null || v === undefined) {
          return this.$i18n_t('Common.fieldRequired');
        }
        if (typeof v === 'string' && v.trim() === '') {
          return this.$i18n_t('Common.fieldRequired');
        }
        if (typeof v === 'number' && Number.isNaN(v)) {
          return this.$i18n_t('Common.fieldRequired');
        }
        return true;
      };
    },
    emailRule() {
      return v => !v || /.+@.+\.[a-z]+/.test(v) || this.$i18n_t('Common.emailValid');
    },
    hexRule() {
      return v => {
        const regExp = new RegExp('^[0-9A-Fa-f]+$', 'g');
        const trimmedValue = (v || '').trim();
        const isHexValid = regExp.exec(trimmedValue) !== null;
        const isLengthEven = trimmedValue.length % 2 === 0;
        if (!this.hasVirtualDeviceKeyReaderPermission && v === PASSWORD_REPLACEMENT) {
          return true;
        }
        return isHexValid && isLengthEven || this.$i18n_tc('DigitalTwin.DigitalTwin.copy.hexRule');
      };
    },
    detailsMode() {
      return this.bridgeHasValidParameters === true ? 'auto' : false;
    },
    textFieldAppendIcon() {
      if (this.fieldConfigStringField === 'password') {
        return this.isValidPassword ? this.showPassword ? 'fal fa-eye' : 'fal fa-eye-slash' : undefined;
      }
      if (this.fieldConfigStringField === 'eui') {
        return 'fal fa-pen';
      }
      return undefined;
    },
    enumItems() {
      if (this.fieldConfig.type === 'enum') {
        if (this.fieldConfig.outputValues && typeof this.fieldConfig.outputValues === 'object') {
          let items = [];
          Object.keys(this.fieldConfig.outputValues).forEach(key => {
            items.push({
              text: this.fieldConfig.outputValues[key],
              value: key
            });
          });
          return items;
        }
      }
      return this.fieldConfig.values;
    },
    keyValueItems() {
      if (this.fieldConfig.type === 'jsonkeyvalue') {
        let items = [];
        if (typeof this.value === 'string') {
          items = Object.entries(JSON.parse(this.value));
        }
        return items;
      }
      return [['', '']];
    },
    // was not used anywhere, remove it it works out fine without it
    // isBridgeValidationsEmpty() {
    //   return Object.keys(this.bridgeValidations).length === 0;
    // },
    isBrigdeHasError() {
      return this.brigdeHasError;
    }
  },
  watch: {
    value() {
      this.v = this.value;
      if (this.fieldConfig.type === 'object') {
        if (get(this.v, 'text') === undefined || get(this.v, 'value') === undefined) {
          try {
            this.v = JSON.stringify(this.v);
          } catch (_) {
            return;
          }
        }
      }
    },
    search(val) {
      if (this.fieldConfig.dependsOnField) {
        // skip onSearch, it is triggered by other field value change
        return;
      }
      val && val !== this.value && this.debouncedRequestAutoComplete(val, this);
    },
    dependsOnFieldValue(val) {
      if (val && this.fieldConfig.autocomplete) {
        this.debouncedRequestAutoComplete(val, this);
      }
    }
  },
  // for newly created config-fields (aka new bridge-instances) the "mounted" hook does not seem to trigger
  // so update the values within the `updated` hook, which is called more often, but we only care about
  // the initial time when this.v is undefined for configuring default data
  updated() {
    this.$nextTick(function () {
      if (this.initialUpdate) {
        return;
      }
      this.initialUpdate = true;
      this.v = this.value;
      if (this.v === undefined) {
        this.v = this.fieldConfig.default;
        if (this.v) {
          this.modelChanged();
        }
      }
      if (this.v === undefined && this.fieldConfig.fallbackConfigValue) {
        this.v = get(this.bridgeConfig, this.fieldConfig.fallbackConfigValue);
        if (this.v) {
          this.modelChanged();
        }
      }
      if (this.fieldConfig.autocomplete) {
        let searchValue = this.v;
        if (this.fieldConfig.dependsOnField) {
          searchValue = this.dependsOnFieldValue;
        }
        this.debouncedRequestAutoComplete(get(searchValue, 'text', searchValue), this);
      }
    });
  },
  mounted() {
    this.$nextTick(function () {
      this.v = this.value;
      if (this.v === undefined) {
        this.v = this.fieldConfig.default;
        if (this.dtwinDeviceId && (this.configKey === 'EUI' || this.configKey === 'eui' || this.configKey === 'deviceId')) {
          this.v = this.dtwinDeviceId;
        }
        if (this.v) {
          this.modelChanged();
        }
      }
      if (this.v === undefined && this.fieldConfig.fallbackConfigValue) {
        this.v = get(this.bridgeConfig, this.fieldConfig.fallbackConfigValue);
        if (this.v) {
          this.modelChanged();
        }
      }
      if (this.fieldConfig.autocomplete) {
        let searchValue = this.v;
        if (this.fieldConfig.dependsOnField) {
          searchValue = this.dependsOnFieldValue;
        }
        this.debouncedRequestAutoComplete(get(searchValue, 'text', searchValue), this);
      }
      if (this.fieldConfig.type === 'jsonkeyvalue') {
        if (!this.value || this.value === '{}') {
          this.emptyCustomHeaders = true;
        }
      }
    });
  },
  methods: {
    get,
    euiRule(length = 16) {
      return v => {
        const regExp = new RegExp(`^([0-9A-F]{${length}})$`, 'gi');
        const result = regExp.exec((v || '').trim());
        return result !== null || this.$i18n_tc('DigitalTwin.DigitalTwin.copy.euiRule', length * 4, {
          n: length * 4
        });
      };
    },
    rulesForField(config) {
      let rules = [];
      if (!config.optional && this.isBrigdeHasError) {
        rules.push(this.requiredRule);
      }
      if (config.type === 'string' && get(config, 'stringOptions.field') === 'email') {
        rules.push(this.emailRule);
      }
      if (config.type === 'string' && get(config, 'stringOptions.field') === 'eui') {
        rules.push(this.euiRule(get(config, 'stringOptions.euiLength')));
      }
      if (config.type === 'string' && get(config, 'stringOptions.field') === 'hex') {
        rules.push(this.hexRule);
      }
      return rules;
    },
    canBeStringified(obj) {
      try {
        JSON.stringify(obj);
        return true;
      } catch (e) {
        return false;
      }
    },
    modelChanged() {
      // this bad boy has some history..
      // check out TWIN-682 (now RD-2817, MR 424)
      // and RD-6292 if there are any problems;
      // reworked because sometimes we are trying to
      // convert an object with a circular structure into JSON
      if (this.fieldConfig.type === 'number') {
        this.v = parseFloat(this.v);
      } else if (this.fieldConfig.type === 'object') {
        if (typeof this.v === 'string') {
          try {
            this.v = JSON.parse(this.v);
          } catch (_) {
            return; // If parsing fails, just return
          }
        } else if (!this.canBeStringified(this.v)) {
          return; // Exit if this.v cannot be stringified
        }
      } else if (this.fieldConfigStringField === 'password') {
        if (this.v === '') {
          return; // If password field is empty, just return
        }
      }
      if (typeof this.v === 'string') {
        if (get(this.fieldConfig, 'stringOptions.lowerCase') === true) {
          this.v = `${this.v}`.toLowerCase();
        }
        if (get(this.fieldConfig, 'stringOptions.upperCase') === true) {
          this.v = `${this.v}`.toUpperCase();
        }
        if (get(this.fieldConfig, 'stringOptions.trim') === true) {
          this.v = `${this.v}`.trim();
        }
      }
      if (this.fieldConfig.type === 'jsonkeyvalue') {
        this.updateValueForJson();
      }
      if (this.fieldConfigStringField === 'textarea' && this.fieldConfig.stringOptions.secure) {
        if (this.v === '') {
          return;
        }
      }
      this.$emit('input', this.v);
      this.$emit('changedBridgeParams');
    },
    textFieldClicked(appendIcon) {
      if ((this.fieldConfigStringField === 'password' || this.fieldConfigStringField === 'textarea' && this.fieldConfig.stringOptions.secure) && this.v === PASSWORD_REPLACEMENT) {
        this.v = '';
      }
      if (appendIcon) {
        if (this.fieldConfigStringField === 'password' || this.fieldConfigStringField === 'textarea' && this.fieldConfig.stringOptions.secure) {
          this.showPassword = !this.showPassword;
        }
        if (this.fieldConfigStringField === 'eui') {
          this.randomEui(get(this.fieldConfig, 'stringOptions.euiLength'));
        }
      }
    },
    randomEui(len) {
      this.v = this.randomHexString(len);
      this.modelChanged();
    },
    randomHexString(len) {
      let charSet = 'ABCDEF0123456789';
      let randomString = '';
      for (let i = 0; i < len; i++) {
        let randomPoz = Math.floor(Math.random() * charSet.length);
        randomString += charSet.substring(randomPoz, randomPoz + 1);
      }
      return randomString;
    },
    textFieldBlurred() {
      if ((this.fieldConfigStringField === 'password' || this.fieldConfigStringField === 'textarea' && this.fieldConfig.stringOptions.secure) && this.v === '') {
        this.v = PASSWORD_REPLACEMENT;
        this.showPassword = false;
      }
    },
    requestAutoComplete: (searchItem, instance) => {
      const bridgeConfigManifest = instance.bridgeManifest;
      const configParam = instance.fieldConfig;
      const minLength = get(configParam, 'autocompleteConfig.minLength', 3);
      if (minLength <= 0 || searchItem && `${searchItem}`.length >= minLength) {
        const isInstanceCommand = get(configParam, 'autocompleteConfig.instanceMethod');
        const command = configParam.autocomplete;
        const parameterNames = Object.keys(get(bridgeConfigManifest, `commands.${isInstanceCommand ? 'instance' : 'static'}.${command}.parameters`, {}));
        let parameters = {};
        if (parameterNames.length >= 1) {
          parameters[parameterNames[0]] = searchItem;
        }
        if (isInstanceCommand) {
          return ApiService.bridgeCommand(instance.bridgeConfigId, command, parameters).then(result => {
            if (!result.error && result.data) {
              instance.autoCompleteItems = cloneDeep(result.data);
            }
          });
        }
        return ApiService.staticBridgeCommand(instance.bridgeType, command, parameters).then(result => {
          if (!result.error && result.data) {
            instance.autoCompleteItems = cloneDeep(result.data);
          }
        });
      }
    },
    addKeyValuePair(index) {
      this.keyValueItems.splice(index + 1, 0, ['', '']);
      this.$forceUpdate();
    },
    removeKeyValuePair(index) {
      this.keyValueItems.splice(index, 1);
      this.$forceUpdate();
    },
    updateKeyValuePair(index, newValue, type) {
      if (type === 'key') {
        this.keyValueItems[index][0] = newValue;
      } else if (type === 'value') {
        this.keyValueItems[index][1] = newValue;
      }
      this.$forceUpdate();
    },
    updateValueForJson() {
      if (!this.keyValueItems || this.keyValueItems.length === 0) {
        this.emptyCustomHeaders = true;
      }
      this.v = JSON.stringify(Object.fromEntries(this.keyValueItems));
    },
    initCustomHeaders() {
      if (this.keyValueItems.length === 0) {
        this.keyValueItems.push(['', '']);
        this.emptyCustomHeaders = false;
        this.$forceUpdate();
      }
    }
  }
};